import {
  isInlineActive,
  unwrapInline,
  wrapInline,
} from '../InlinePlugin/utils';
import { Range, Node, type Editor } from 'slate';
import { type LinkElement } from '../../types/aha-editor';
import { useCallback } from 'react';

// TODO: Convert this to a static class for consistency with other utils.
// Does the text of the element equal the URL?
export const textEqualsUrl = (element: LinkElement) => {
  return (element.link_href || '') === Node.string(element);
};

export const isLinkActive = (editor: Editor) => {
  return isInlineActive(editor, 'link');
};

export const unwrapLink = (editor: Editor) => {
  return unwrapInline(editor, 'link');
};

export const wrapLink = (editor: Editor, url: string, text: string) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);

  const buildElement = (): LinkElement => {
    return {
      type: 'link',
      link_href: url,
      children: isCollapsed ? [{ text }] : [],
    };
  };

  wrapInline(editor, buildElement);
};

// There are two character classes: one for characters allowed inside
// a URL, and one for character classes allowed to end a URL. This
// way, you can end a URL with a '.', and have that not be a part of
// the link.
const LINK_REGEXP =
  /((?:https?:\/\/|www\.)[-A-Z0-9+&@#/%=~_|$?!:,.]*[A-Z0-9+&@#/%=~_|$])[^\\s]*$/i;
// https://github.com/angular/angular/blob/916487a873244503d17372761d4b7b577498ac17/packages/core/src/sanitization/url_sanitizer.ts#L36
const SAFE_URL_PATTERN =
  /^(?:(?:https?|mailto|ftp|tel|file|sms):|[^&:/?#]*(?:[/?#]|$))/gi;

export function normalizeHref(href?: string | null) {
  if (href === null || href === undefined) return '';
  if (href.includes(':') || href[0] === '#' || href[0] === '/') {
    if (href.match(SAFE_URL_PATTERN)) {
      return href;
    } else {
      return `unsafe:${href}`;
    }
  }
  const isSecureUrl = href.startsWith('https://');
  return isSecureUrl ? `https://${href}` : `http://${href}`;
}

export function detectLink(
  str: string
): { url: string; offset: number } | undefined {
  const match = str.match(LINK_REGEXP);
  if (match) {
    return {
      url: match[1],
      offset: str.length - match[0].length,
    };
  }
}

export function displayURL(
  url = '',
  opts = { protocol: false, www: false }
): string {
  if (typeof url !== 'string') return '';
  const originalUrl = url;

  // Ensure the URL has a protocol, or default to http://
  if (!/^http(s)?:\/\//.test(url)) url = `http://${url}`;

  try {
    const parsedUrl = new URL(url);
    if (!opts.www) {
      parsedUrl.host = parsedUrl.host.replace(/^www\./, '');
    }
    if (opts.protocol) {
      if (parsedUrl.origin === 'null') {
        // Some URLs may be data URLs, which do not have an origin. (ex tel:1234567890 or mailto:foo@bar.com).
        return parsedUrl.host || parsedUrl.pathname;
      } else {
        return parsedUrl.origin;
      }
    } else {
      return parsedUrl.host;
    }
  } catch (e) {
    // If the URL is invalid, return it as is.
    return originalUrl;
  }
}

// Never consider AI redirects to be same-origin, since they redirect to external sites.
export const isSameHost = (href: string) => {
  try {
    return (
      new URL(href, location.href).origin === location.origin &&
      !href.includes('/ai_redirects')
    );
  } catch (e) {
    return false;
  }
};

const navigateToUrl = (url: string) => {
  if (window.Turbolinks) {
    window.Turbolinks.visit(url);
  } else {
    window.location.assign(url);
  }
};

export interface LinkHandlerOptions {
  href: string;
  linkTarget?: string;
  focused: boolean;
  readOnly: boolean;
}

export const useLinkHandlers = ({
  href,
  linkTarget,
  focused,
  readOnly,
}: LinkHandlerOptions) => {
  const normalizedHref = normalizeHref(href);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Let the mouse down through to focus on the link/show the toolbar
      if (focused || readOnly) {
        return;
      }

      // Force open the link in a new tab
      // We can't rely on the browser's default behaviour here, because if we don't prevent the default, the
      // editor will become focused instead of opening the link.
      if (e.metaKey || e.ctrlKey) {
        window.open(normalizedHref, '_blank');
        e.preventDefault();
        return;
      }

      // Since we're not focused, don't let the editor become focused, then fall through to default click handler.
      if (
        // If the link element explicitly has a target, we will use it
        !linkTarget &&
        href !== '#' &&
        (href[0] === '#' || isSameHost(normalizedHref))
      ) {
        e.preventDefault();
        return;
      }

      // Open the link in a new tab.
      window.open(normalizedHref, '_blank');
      e.preventDefault();
      e.stopPropagation();
    },
    [focused, readOnly, linkTarget, normalizedHref, href]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (readOnly) {
        // Read-only editors should act as normal documents, so fall
        // back to the default behavior
        return;
      }

      // Prevent the click in focused state so we show the toolbar
      // instead of opening the link.
      if (focused) {
        // We must preventDefault to stop Turbolinks from following it
        e.preventDefault();
        return;
      }

      // If there's no URL, do nothing
      if (href === '#') {
        e.preventDefault();
        return;
      }

      if (href !== '#' && (href[0] === '#' || isSameHost(normalizedHref))) {
        // Navigate to the link in this tab.
        if (isSameHost(normalizedHref) && href.includes('#')) {
          try {
            const url = new URL(normalizedHref, window.location.href);
            if (url.origin === window.location.origin) {
              if (url.pathname === window.location.pathname) {
                window.location.href = url.hash;
              } else {
                navigateToUrl(normalizedHref);
              }
            }
          } catch (e) {
            console.error(e);
          }
        } else {
          navigateToUrl(normalizedHref);
        }
      } else {
        // Open the link in a new tab.
        window.open(normalizedHref, '_blank');
      }
    },
    [focused, readOnly, normalizedHref, href]
  );

  return { handleMouseDown, handleClick, normalizedHref };
};

export const getLinkAttributes = (href: string, linkTarget?: string) => {
  const extraAttrs: { target?: string; rel?: string } = {};

  if (href[0] !== '#') {
    if (linkTarget !== '_self') {
      extraAttrs.target = '_blank';
    }

    extraAttrs.rel = 'noopener noreferrer';
  }

  return extraAttrs;
};
